import { isEqual } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikValues } from 'formik';

import { mapQuery } from 'utils';
import { useCurrentQuery } from 'store/api/tenants';

import { Icon } from '../icons';
import { Details } from '../details';

import { AuthLayoutProps } from './types';

export function AuthLayout<Value extends FormikValues>({
  title,
  children,
  onSubmit,
  className,
  logoHidden,
  initialValues,
  lowerTitleSize,
  wrapperClassName,
  containerClassName,
  ...formikProps
}: AuthLayoutProps<Value>) {
  const { data, ...props } = useCurrentQuery(undefined, { skip: logoHidden });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const valuesRef = useRef<Value>(initialValues);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onChangeValues = (values: Value) => {
    const isChanged = !isEqual(values, valuesRef.current);
    valuesRef.current = values;
    if (isChanged) {
      resetMessages();
    }
  };

  const renderedMessage = errorMessage || successMessage;

  return (
    <Details {...mapQuery(props)}>
      <div
        className={twMerge(
          "flex min-h-screen w-screen items-center justify-center overflow-y-auto bg-[url('/images/bg.svg')] bg-cover bg-center py-10",
          className
        )}
      >
        <div
          className={twMerge(
            'sm:bg-white-primary sm:border-gray-primary sm:shadow-primary flex h-fit w-[504px] flex-col rounded-xl p-4 sm:border-[1.5px] sm:p-6 lg:justify-center',
            wrapperClassName
          )}
        >
          {!logoHidden && data?.logo && (
            <div className="flex justify-center gap-4">
              <div className="w-20">
                <img
                  src={data.logo}
                  alt={data.name}
                  className="h-auto w-full object-contain"
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap">
            <div
              className={twMerge(
                'flex w-full flex-col',
                !logoHidden && `mt-6`,
                containerClassName
              )}
            >
              <Typography
                variant="h1"
                className="mb-4 text-center lg:mb-6"
                sx={{
                  fontWeight: { xs: 600, lg: 700 },
                  fontSize: {
                    xs: '18px',
                    lg: lowerTitleSize ? '24px' : '40px',
                  },
                  lineHeight: {
                    xs: '28px',
                    lg: lowerTitleSize ? '32px' : '48px',
                  },
                }}
              >
                {title}
              </Typography>

              {renderedMessage && (
                <div
                  className={twMerge(
                    'mb-3 flex items-center gap-3 rounded-xl px-4 py-3',
                    !!successMessage && 'bg-green-50',
                    !!errorMessage && 'bg-red-secondary-primary'
                  )}
                >
                  {errorMessage ? (
                    <Icon.Info
                      width={24}
                      height={24}
                      className="[&_path]:fill-red-primary-primary rotate-180"
                    />
                  ) : (
                    <div
                      className="h-6 w-6 bg-green-500"
                      style={{
                        mask: `url('/images/check-circle.svg') no-repeat center / contain`,
                        WebkitMask: `url('/images/check-circle.svg') no-repeat center / contain`,
                      }}
                    />
                  )}
                  <Typography>{renderedMessage}</Typography>
                </div>
              )}

              <div className="flex w-full items-center justify-center">
                <Formik<Value>
                  {...formikProps}
                  initialValues={initialValues}
                  onSubmit={async (values) => {
                    resetMessages();
                    const result = onSubmit(values);
                    if (result instanceof Promise) {
                      const promiseResult = await result;
                      if (promiseResult && typeof promiseResult === 'object') {
                        if ('successMessage' in promiseResult) {
                          setSuccessMessage(promiseResult.successMessage);
                        }
                        if ('errorMessage' in promiseResult) {
                          setErrorMessage(promiseResult.errorMessage);
                        }
                      }
                      return promiseResult;
                    } else {
                      return result;
                    }
                  }}
                >
                  {(props) => {
                    const render =
                      // @ts-ignore
                      children instanceof Function ? children(props) : children;

                    onChangeValues(props.values);

                    return (
                      <div className="w-full">
                        <Form>{render}</Form>
                      </div>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Details>
  );
}
